import { observer } from 'mobx-react-lite'
import { getUnixTime } from 'date-fns/getUnixTime'

import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import Stack from '@mui/material/Stack'

import { Text } from '@mxenabled/mxui'

import AccountListItem from '../../accounts/components/AccountListItem'
import DetailsFooter from './DetailsFooter'
import DetailsExpenseRow, { ExpenseType } from './DetailsExpenseRow'
import DetailsExpenseHeader from './DetailsExpenseHeader'

import { useAppConfig, useGlobalCopyStore, useWidgetContainerProvider } from '../../common'
import { DATE_FORMATS_INTL, formatDate } from '../../core/constants'
import { type Account, type Recurrence } from '../../core'
import { useCashflowUiStore } from '../context/hooks'
import { recurrenceFilter } from '../util/Filters'
import { getAccountStartingBalance } from '../../core/utils/AccountUtils'

const DetailsTab = () => {
  const { availableWidth } = useWidgetContainerProvider()
  const { cashflow: customCopy } = useGlobalCopyStore()
  const {
    config: { display_available_balance_in_accounts: useAvailable },
  } = useAppConfig()
  const { dateRange, selectedCashAccounts, upcomingExpenses, upcomingIncome } = useCashflowUiStore()

  return (
    <Stack>
      <Stack direction={availableWidth < 400 ? 'column' : 'row'} justifyContent="space-between">
        <Text bold={true} sx={{ fontSize: 15, mb: 12 }}>
          {customCopy.accounts_breakdown}
        </Text>
        <Text bold={true} sx={{ fontSize: 15, mb: 12 }}>
          {formatDate(dateRange.start, DATE_FORMATS_INTL.FULL_MONTH_DAY)} -{' '}
          {formatDate(dateRange.end, DATE_FORMATS_INTL.FULL_MONTH_DAY)}
        </Text>
      </Stack>

      <Stack>
        {selectedCashAccounts.map((account: Account) => {
          const upcomingIncomeByAccount = upcomingIncome
            .filter(recurrenceFilter(account, dateRange))
            .sort((a, b) => getUnixTime(a.expectedDate) - getUnixTime(b.expectedDate))

          const projectedExpensesByAccount = upcomingExpenses.filter(
            recurrenceFilter(account, dateRange),
          )

          const incomeTotal = upcomingIncomeByAccount.reduce(
            (acc: number, curr: Recurrence) => acc + curr.amount,
            0,
          )
          const expenseTotal = projectedExpensesByAccount.reduce(
            (acc, curr) => acc + curr.amount,
            0,
          )

          const remainingBalance =
            getAccountStartingBalance(account, useAvailable) + incomeTotal - expenseTotal

          return (
            <List
              key={account.guid}
              sx={{
                border: '1px solid',
                borderColor: 'neutral.light',
                mb: 24,
                borderRadius: 1,
              }}
            >
              <AccountListItem
                account={account}
                listItemProps={{
                  disableGutters: false,
                  disablePadding: false,
                  sx: { borderTopRadius: 1, pr: 24 },
                }}
              />
              <Divider />
              {/* Income */}
              {upcomingIncomeByAccount.length > 0 && (
                <ListItem>
                  <List
                    subheader={
                      <DetailsExpenseHeader total={incomeTotal} type={ExpenseType.INCOME} />
                    }
                    sx={{ width: '100%' }}
                  >
                    {upcomingIncomeByAccount.map((r) => (
                      <DetailsExpenseRow key={r.repeatingTransactionGuid} recurrence={r} />
                    ))}
                  </List>
                </ListItem>
              )}
              {/* Expenses */}
              {projectedExpensesByAccount.length > 0 && (
                <ListItem>
                  <List
                    subheader={
                      <DetailsExpenseHeader total={expenseTotal} type={ExpenseType.EXPENSE} />
                    }
                    sx={{ width: '100%' }}
                  >
                    {projectedExpensesByAccount.map((r) => (
                      <DetailsExpenseRow key={r.repeatingTransactionGuid} recurrence={r} />
                    ))}
                  </List>
                </ListItem>
              )}
              {/* Bottom row total */}
              <DetailsFooter remaining={remainingBalance} />
            </List>
          )
        })}
      </Stack>
    </Stack>
  )
}

export default observer(DetailsTab)
